import React from 'react';
import { z } from 'zod';

import {
	type CheckoutDisclosure,
	checkoutDisclosureSchema,
	CREDIT_APP_DISCLAIMER,
	PROGRAM_SECTIONS,
	type ProgramOwnerCheckoutDisclosuresDto,
	type ProgramOwnerServiceConfig,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';
import { Divider,Form, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateCheckoutDisclosures } from '../../../../api/configuration/services/useUpdateCheckoutDisclosures';
import { useServiceProgramInfo } from '../../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../../config-mgmt/FormSection';

/**
 * Default system disclosures that are hardcoded in the checkout component.
 * These are shown to all buyers during checkout and are separate from custom disclosures.
 *
 * These match the system disclosures in checkout/facit-lite/src/components/checkoutInfoComponents/DisclosureAgreementSection.jsx
 * Note: Some disclosures are conditionally shown based on payment/registration options,
 * but these represent the full set of system disclosures that may appear.
 */
const DEFAULT_CHECKOUT_DISCLOSURES: CheckoutDisclosure[] = [
	{
		name: 'Payment Authorization',
		description:
			'The dealer ("Dealer") has authorized Ekho Inc. to accept and send payments through Ekho\'s platform on behalf of Dealer for goods and services. Payment made through Ekho\'s platform will be treated as payment to Dealer.',
	},
	{
		name: 'In-person Purchase',
		description:
			"In-person product availability and pricing may differ from what's shown online. Any required DMV paperwork will be completed at the dealership.",
	},
	{
		name: 'Credit Application',
		description: CREDIT_APP_DISCLAIMER,
	},
	{
		name: 'Servicing',
		description:
			"You acknowledge and agree that any servicing you might need, as it pertains to warranty work or otherwise, requires that you interface with the program owner and/or any 3rd party service providers that the program owner has partnered with, and that Ekho is not responsible for the ongoing servicing of any program owner vehicle(s). Review the program owner's Limited Warranty for details.",
	},
	{
		name: 'Sale Counterpart',
		description:
			'You acknowledge and agree that you are purchasing the vehicle(s) from the dealer, who is licensed to sell the program owner vehicles in the state you have specified above.',
	},
	{
		name: 'Refunds',
		description:
			'Refund policy varies based on payment type and vehicle configuration. Please review the specific refund terms shown during checkout.',
	},
	{
		name: 'Taxes and Registration Fees',
		description:
			'You agree and acknowledge that the tax and registration fees shown before placing your order are estimates only and that the actual tax and registration fees that you are charged may vary.',
	},
	{
		name: 'Tax and Fee Penalties When Self-Registering',
		description:
			'You acknowledge and agree that, since you are self-registering the vehicle(s), you are responsible for paying the state taxes and fees associated with the registration process, which - depending on the state - may be due to the state within a certain number of days after the date of purchase or date of first operation, and that penalties may apply if you fail to pay these taxes and fees on time.',
	},
	{
		name: 'Communication from Ekho and Program Owner',
		description:
			'You agree to receive automated and personalized text messages from Ekho and the program owner related to your purchase or inquiry. Message frequency varies and depends on the status of your order or inquiry. Message & data rates may apply. For more information view Ekho\'s <a href="https://www.ekhodealer.com/legal/terms-conditions" target="_blank" rel="noreferrer">terms</a> & <a href="https://www.ekhodealer.com/legal/privacy-policy" target="_blank" rel="noreferrer">privacy policy</a> and the program owner\'s website. Reply STOP to any Ekho text messages to opt out. Contact the program owner to opt out of their communications.',
	},
	{
		name: 'Payment Disputes',
		description:
			'Buyer understands and agrees this is a motor vehicle purchase, and credit card & ACH charges can not be disputed for any reason other than fraud. Due to state titling and registration regulations, the vehicle(s) can not be returned for any reason.',
	},
	{
		name: 'Financing with Ekho',
		description:
			'Not all customers will qualify for financing terms. It can take longer than a few minutes to receive a financing decision if you apply for financing outside of business hours or if the lender requires additional information.',
	},
	{
		name: 'Financing with Klarna',
		description:
			'Rates range from 7.99%-33.99% APR based on creditworthiness and subject to credit approval, resulting in, for example, 12 equal monthly payments of $86.98 at 7.99% APR to $99.46 at 33.99% APR per $1000 borrowed. Minimum purchase required. A down payment may be required. Estimation of monthly payment excludes potential tax and shipping costs. Monthly financing through Klarna issued by WebBank.',
	},
];

/**
 * Modal content listing the default system disclosures shown to all buyers during checkout.
 */
function DefaultDisclosuresModalContent() {
	return (
		<Stack itemsSpacing='16'>
			{DEFAULT_CHECKOUT_DISCLOSURES.map((disclosure, index) => (
				<React.Fragment key={disclosure.name}>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall' weight='semibold'>
							{disclosure.name}
						</Text>
						<div
							dangerouslySetInnerHTML={{ __html: disclosure.description }}
							style={{
								fontSize: 'var(--bs-font-size-sm)',
								color: 'var(--bs-secondary)',
							}}
						/>
					</Stack>
					{index < DEFAULT_CHECKOUT_DISCLOSURES.length - 1 && <Divider />}
				</React.Fragment>
			))}
		</Stack>
	);
}

const disclosuresSchema = z.object({
	disclosures: z.array(checkoutDisclosureSchema).default([]),
});

/**
 * Helper function to get initial form data for checkout disclosures
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): z.infer<typeof disclosuresSchema> {
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const checkoutConfig = programOwnerConfig?.checkout;

	// Validate that disclosures is a valid array of objects
	const rawDisclosures = checkoutConfig?.disclosures;
	const isValidDisclosuresArray =
		Array.isArray(rawDisclosures) &&
		rawDisclosures.every(
			(item) => item !== null && typeof item === 'object' && !Array.isArray(item),
		);

	return {
		disclosures: isValidDisclosuresArray ? rawDisclosures : [{ name: '', description: '' }],
	};
}

export function CheckoutDisclosuresForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(
		PROGRAM_SECTIONS.CHECKOUT,
		SUBSECTIONS.CHECKOUT_DISCLOSURES,
	);
	const updateCheckoutDisclosures = useUpdateCheckoutDisclosures();

	// Early return if subsection is not part of the program or is Ekho-only
	if (!programInfo.shouldRender) {
		return null;
	}

	const handleSubmit = async (
		data: z.infer<typeof disclosuresSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			const parsedData = disclosuresSchema.parse(data);
			const dataToSubmit: ProgramOwnerCheckoutDisclosuresDto = parsedData.disclosures;

			await updateCheckoutDisclosures.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			console.error('Failed to update checkout disclosures:', error);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.PROGRAM,
				section: PROGRAM_SECTIONS.CHECKOUT,
				subsection: SUBSECTIONS.CHECKOUT_DISCLOSURES,
			}}
			form={{
				schema: disclosuresSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Disclosures'
			description='Custom legal disclosures shown to buyers before completing checkout. These are shown alongside any vehicle-specific disclosures.'
			formTitle='Custom disclosures'
			formDescription={{
				children: 'System disclosures are shown separately and cannot be customized.',
				modal: {
					linkText: 'View default disclosures',
					component: <DefaultDisclosuresModalContent />,
				},
			}}>
			<Form.Field.ObjectArray
				schemaField='disclosures'
				fields={[
					{
						key: 'name',
						label: 'Name',
						type: 'text',
						placeholder: 'e.g., Warranty Terms',
						required: true,
					},
					{
						key: 'description',
						label: 'Description',
						type: 'textarea',
						placeholder: 'You acknowledge and agree that...',
						required: true,
					},
				]}
				entryLimit={20}
				emptyText='No disclosures added'
			/>
		</FormSection>
	);
}

export default CheckoutDisclosuresForm;
